import React, { FC } from 'react';
import withStyles from 'src/common/withStyles';

import { decorateComponent, getClassNameFor } from '../../helpers';
import ListingPhoto from '../presentation/ListingPhoto';
import RatingInput from '../inputs/RatingInput';
import StarIcon from '../StarIcon';
import SuperOwner from '../SuperOwner';
import UniversalLink from '../UniversalLink';
import Breadcrumbs from '../Breadcrumbs';

import s from './ListingCard.module.scss';
import { BoatDetail } from '../../../types/boat/BoatDetail';
import { PrimaryCategory } from '../../../types/boat/PrimaryCategory';

const getFontSizeModifier = (priceString: string): string => {
  let fontSize = 16;
  if (priceString.length > 8) {
    fontSize = priceString.length <= 12 ? 16 - (priceString.length - 8) : 11;
  }
  return `font-${fontSize}`;
};

export type ListingCardProps = {
  breadcrumbs?: BoatDetail['breadcrumbs'];
  capacity: number;
  category?: PrimaryCategory;
  classNameModifier?: string;
  formattedPrice: string;
  headline: string;
  id: string;
  isSuperOwner: boolean;
  listingUrl: string;
  photoUrl: string;
  priceType: string;
  rating: number;
};

const ListingCard: FC<ListingCardProps> = ({
  breadcrumbs,
  capacity,
  classNameModifier,
  formattedPrice,
  headline,
  id,
  listingUrl,
  photoUrl,
  priceType,
  rating,
  category,
  isSuperOwner,
}) => {
  const emptyStar = (
    <StarIcon
      classNameModifier="small cold bordered"
    />
  );
  const fullStar = (
    <StarIcon
      classNameModifier="small full bordered"
    />
  );
  const priceValueClassName = getClassNameFor(s, 'priceValue', getFontSizeModifier(formattedPrice));

  return (
    <div
      id={id}
      className={getClassNameFor(s, 'root', classNameModifier)}
      data-test="ListingCard"
    >
      <div className={s.details}>
        <ListingPhoto
          listingUrl={listingUrl}
          photoUrl={photoUrl}
          headline={headline}
          classNameModifier={classNameModifier}
        />
        <div className={s.price}>
          <small className={s.priceType}>
            {priceType}
          </small>
          <strong className={priceValueClassName}>
            {formattedPrice}
          </strong>
        </div>
        {rating > 0 && (
          <RatingInput
            readonly
            withTextRating={false}
            initialRating={rating}
            classNameModifier="listingCard oneLine"
            emptySymbol={emptyStar}
            fullSymbol={fullStar}
          />
        )}
      </div>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs classNameModifier="listingCard">
          {breadcrumbs.map(({ name }) => name)}
          {category?.name && (
            category.short_name || category.name
          )}
        </Breadcrumbs>
      )}
      <div className={s.keyFeatures}>
        {isSuperOwner && (
          <SuperOwner classNameModifier="listingCard" />
        )}
        <span className={s.capacity}>
          {`${capacity} guest${capacity > 1 ? 's' : ''}`}
        </span>
      </div>
      <h3 className={s.headline}>
        <UniversalLink
          to={listingUrl}
          className={s.link}
          external
        >
          {headline}
        </UniversalLink>
      </h3>
    </div>
  );
};

const decorators = [
  withStyles(s),
];

export default decorateComponent(ListingCard, decorators);
